import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender";
import { AavoMosaic } from "src/components/common/mosaic/AavoMosaic";
import i18n from "i18next";
import { PartConfigurationTopLevelDataView } from "src/components/views/erp/configurator/inspecting/PartConfigurationTopLevelDataView.tsx";
import { PartConfigurationPropertiesDataGrid } from "./PartConfigurationPropertiesDataGrid";
import { DocumentsOfSingleObjectView } from "src/components/views/documents/objectDocuments/DocumentsOfSingleObjectView.tsx";
import { PartConfigurationBomDataGrid } from "./PartConfigurationBomDataGrid";
import { PartConfigurationOperationsDataGrid } from "src/components/views/erp/configurator/inspecting/PartConfigurationOperationsDataGrid.tsx";
import { PartConfigurationAttributesDataGrid } from "src/components/views/erp/configurator/inspecting/PartConfigurationAttributesDataGrid.tsx";
import { PartConfigurationBomPricesByGroupsDataGrid } from "src/components/views/erp/configurator/inspecting/PartConfigurationBomPricesByGroupsDataGrid.tsx";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import {
	PartConfigurationDetailsViewApi,
	PartConfigurationDetailsViewApi_PartConfigurationDto
} from "src/api/generated/erp/configurator/api/partConfigurationDetailsViewApi.ts";
import { PartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/partConfiguratorType.ts";
import { ConfigurationManualEditContext } from "src/api/generated/erp/configurator/manualEdit/configurationManualEditContext.ts";

export interface PartConfigurationDetailsViewProps {
	partConfigurationId: number;
	partConfiguratorType: PartConfiguratorType;
	showBom?: boolean;
	vatPercent?: number;
	manualEditContext?: ConfigurationManualEditContext;
}

export const PartConfigurationDetailsView = (props: PartConfigurationDetailsViewProps) => {
	const { partConfigurationId, partConfiguratorType } = props;
	const manualEditContext =
		props.manualEditContext ??
		{
			rootPartConfigurationId: partConfigurationId,
			rootPartConfiguratorType: partConfiguratorType,
			partConfigurationBomLinePath: [],
		};
	return (
		<AsyncFetchRender
			fetch={() => PartConfigurationDetailsViewApi.getPartConfiguration({ partConfigurationId })}
			content={(partConfiguration, refreshData) => (
				<Content
					{...props}
					partConfiguration={partConfiguration}
					manualEditContext={manualEditContext}
					refreshData={refreshData}
				/>
			)}
		/>
	);
};

interface ContentProps extends PartConfigurationDetailsViewProps {
	partConfiguration: PartConfigurationDetailsViewApi_PartConfigurationDto;
	manualEditContext: ConfigurationManualEditContext;
	refreshData: () => Promise<unknown>;
}

const Content = ({
	partConfigurationId,
	partConfiguration,
	partConfiguratorType,
	showBom = true,
	vatPercent,
	manualEditContext,
	refreshData,
}: ContentProps) => {
	const { tenantConfig } = useTenantCustomizations();
	const hasPermission = useHasPermission();
	const manualEditingEnabled =
		partConfiguration.isEditable && hasPermission(Permissions.Parts.PartConfigurations.MANAGE);

	return (
		<AavoMosaic
			viewId={"49778551B880217B"}
			layout={{
				type: "tabs",
				items: [
					{
						title: i18n.t("configuration"),
						type: "row",
						first: {
							type: "column",
							first: {
								type: "panel",
								title: i18n.t("configuration"),
								content: (
									<PartConfigurationTopLevelDataView
										partConfiguration={partConfiguration}
										partConfiguratorType={partConfiguratorType}
										refreshData={refreshData}
									/>
								),
							},
							second: {
								type: "panel",
								title: i18n.t("bom_pricing"),
								content: (
									<PartConfigurationBomPricesByGroupsDataGrid
										partConfiguration={partConfiguration}
										vatPercent={vatPercent}
									/>
								),
							},
						},
						second: {
							type: "panel",
							title: i18n.t("properties"),
							content: <PartConfigurationPropertiesDataGrid partConfigurationId={partConfigurationId} />,
						},
					},
					{
						title: i18n.t("bom"),
						hidden: !showBom,
						type: "row",
						first: {
							type: "panel",
							title: i18n.t("bom"),
							content: (
								<PartConfigurationBomDataGrid
									partConfiguration={partConfiguration}
									partConfiguratorType={partConfiguratorType}
									manualEditContext={manualEditContext}
									editable={manualEditingEnabled}
									vatPercent={vatPercent}
								/>
							),
						},
						second: {
							type: "panel",
							title: i18n.t("operations"),
							content: (
								<PartConfigurationOperationsDataGrid
									partConfiguration={partConfiguration}
									editable={manualEditingEnabled}
									manualEditContext={manualEditContext}
								/>
							),
						},
					},
					tenantConfig.documentsEnabled && {
						title: i18n.t("documents"),
						type: "panel",
						content: (
							<DocumentsOfSingleObjectView
								objectRef={{
									objectType: "PART_CONFIGURATION",
									objectId: partConfigurationId,
								}}
								editable={partConfiguration.isEditable}
							/>
						),
					},
					{
						title: i18n.t("attributes"),
						type: "panel",
						content: (
							<PartConfigurationAttributesDataGrid
								partConfigurationId={partConfiguration.partConfigurationId}
								editable={hasPermission(Permissions.Parts.PartConfigurations.MANAGE)}
								manualEditContext={manualEditContext}
							/>
						),
					},
				],
			}}
		/>
	);
};
